/**
 * @file integrateCorsSystem.js
 * @description Punto de integración único para implementar la solución CORS en Nubelytics
 * @version 1.0.0
 * @author Nubelytics Engineering Team
 */

import axios from 'axios';
import { initCorsSolution } from './index';
import corsProxyConfig from './CorsProxyConfig';
import corsTelemetry from './CorsTelemetry';
import { AutoCorsProvider } from './AutoCorsProvider';

/**
 * Integra la solución CORS completa en la aplicación Nubelytics
 * 
 * Esta función debe ser llamada en el punto de entrada de la aplicación
 * antes de cualquier petición HTTP. Configura automáticamente los interceptores
 * para todas las instancias conocidas de Axios y prepara el sistema para
 * detectar y solucionar problemas CORS.
 * 
 * @param {Object} options Opciones de configuración
 * @param {boolean} [options.enableTelemetry=true] Habilitar telemetría de eventos CORS
 * @param {boolean} [options.withReactProvider=true] Incluir proveedor de contexto React
 * @param {boolean} [options.autoDetectProblems=true] Detectar automáticamente problemas CORS
 * @returns {Object} Objeto con componentes y funciones de la solución CORS
 */
function integrateCorsSystem({
  enableTelemetry = true,
  withReactProvider = true,
  autoDetectProblems = true
} = {}) {
  console.info('[CORS] Integrando sistema CORS avanzado en Nubelytics...');
  
  // 1. Configurar telemetría
  corsProxyConfig.ENABLE_TELEMETRY = enableTelemetry;
  
  // 2. Inicializar interceptores centralizados
  const corsRegistry = initCorsSolution();
  
  // 3. Añadir compatibilidad con sistema OAuth2
  corsProxyConfig.IMPORTANT_HEADERS.push(
    'authorization',
    'x-auth-token',
    'client_id',
    'client_secret'
  );
  
  // 4. Añadir dominios de OAuth2 a la lista segura para evitar proxying innecesario
  // Asegurar que el método addSafeDomain existe antes de llamarlo
  if (typeof corsProxyConfig.addSafeDomain === 'function') {
    corsProxyConfig.addSafeDomain('auth.nubelytics.com');
    corsProxyConfig.addSafeDomain('login.nubelytics.com');
  } else {
    console.warn('[CORS] El método addSafeDomain no está disponible, utilizando método alternativo');
    // Método alternativo para añadir dominios seguros si no existe addSafeDomain
    if (corsProxyConfig._safeDomains && Array.isArray(corsProxyConfig._safeDomains)) {
      ['auth.nubelytics.com', 'login.nubelytics.com'].forEach(domain => {
        if (!corsProxyConfig._safeDomains.includes(domain)) {
          corsProxyConfig._safeDomains.push(domain);
        }
      });
      
      // Guardar en localStorage si está disponible
      try {
        if (typeof localStorage !== 'undefined') {
          localStorage.setItem('nubelytics_safe_domains', JSON.stringify(corsProxyConfig._safeDomains));
        }
      } catch (e) {
        console.error('[CORS] Error al guardar dominios seguros:', e);
      }
    }
  }
  
  if (autoDetectProblems) {
    // Cargar errores previos y configurar detección automática
    _loadPreviousCorsErrors();
  }
  
  // Registrar evento de integración completa
  corsTelemetry.trackEvent('cors_system_integrated', {
    timestamp: Date.now(),
    withReactProvider,
    enableTelemetry,
    autoDetectProblems
  });
  
  console.info('[CORS] Sistema CORS integrado correctamente');
  
  /**
   * Prueba de diagnóstico para el sistema CORS
   * @returns {Object} Resultado del diagnóstico
   */
  const runDiagnosticTest = async () => {
    console.info('[CORS] Ejecutando test diagnóstico del sistema CORS...');
    let results = {
      timestamp: Date.now(),
      success: false,
      corsConfig: {
        enableTelemetry,
        withReactProvider,
        autoDetectProblems,
        hasInterceptors: !!corsRegistry?.hasRegisteredInterceptors,
        safeDomains: Array.isArray(corsProxyConfig._safeDomains) ? corsProxyConfig._safeDomains : [],
        forcedDomains: Array.isArray(corsProxyConfig._alwaysUseProxyDomains) ? corsProxyConfig._alwaysUseProxyDomains : []    
      },
      tests: {}
    };
    
    // Test 1: Verificar inicialización correcta
    results.tests.initialization = {
      success: true,
      corsRegistryAvailable: !!corsRegistry,
      corsConfigAvailable: !!corsProxyConfig,
      corsTelemetryAvailable: !!corsTelemetry
    };
    
    // Test 2: Verificar que los métodos críticos están disponibles
    results.tests.methods = {
      success: false,
      available: {}
    };
    
    // Verificar métodos en corsProxyConfig
    const requiredMethods = [
      'shouldAlwaysUseProxy', 'generateProxyUrl', 'addDomainToForceList', 
      'removeDomainFromForceList', 'isSafeDomain', 'addSafeDomain', 'removeSafeDomain'
    ];
    
    for (const method of requiredMethods) {
      results.tests.methods.available[method] = typeof corsProxyConfig[method] === 'function';
    }
    
    results.tests.methods.success = Object.values(results.tests.methods.available).every(Boolean);
    
    // Marcar éxito global si todos los tests pasaron
    results.success = results.tests.initialization.success && results.tests.methods.success;
    
    console.info(`[CORS] Test diagnóstico completado: ${results.success ? 'EXITOSO' : 'FALLIDO'}`);
    return results;
  };
  
  return {
    // Componentes React
    AutoCorsProvider,
    
    // Módulos principales
    corsRegistry,
    corsTelemetry,
    corsProxyConfig,
    
    // Métodos de diagnóstico
    runDiagnosticTest,
    
    // Métodos útiles - Autenticación y proxy seguro
    addDomainToForceList: corsProxyConfig.addDomainToForceList,
    removeDomainFromForceList: corsProxyConfig.removeDomainFromForceList,
    generateProxyUrl: corsProxyConfig.generateProxyUrl,
    
    // Métodos de dominios seguros - OAuth2
    addSafeDomain: corsProxyConfig.addSafeDomain,
    removeSafeDomain: corsProxyConfig.removeSafeDomain,
    isSafeDomain: corsProxyConfig.isSafeDomain
  };
}

/**
 * Carga errores CORS previos y configura los dominios problemáticos
 * @private
 */
function _loadPreviousCorsErrors() {
  try {
    if (typeof localStorage !== 'undefined') {
      const corsErrors = localStorage.getItem('nubelytics_cors_errors');
      if (corsErrors) {
        try {
          const errors = JSON.parse(corsErrors);
          
          // Analizar errores recientes (última semana)
          const oneWeekAgo = Date.now() - (7 * 24 * 60 * 60 * 1000);
          const recentErrors = errors.filter(err => err.timestamp > oneWeekAgo);
          
          // Agrupar por dominio
          const domainCounts = {};
          for (const error of recentErrors) {
            if (error.domain) {
              domainCounts[error.domain] = (domainCounts[error.domain] || 0) + 1;
            }
          }
          
          // Si un dominio tiene más de 3 errores, añadirlo a la lista de forzado
          let autoAddedDomains = 0;
          for (const [domain, count] of Object.entries(domainCounts)) {
            if (count >= 3) {
              if (corsProxyConfig.addDomainToForceList(domain)) {
                autoAddedDomains++;
              }
            }
          }
          
          if (autoAddedDomains > 0) {
            console.info(`[CORS] Auto-añadidos ${autoAddedDomains} dominios problemáticos basados en errores previos`);
          }
        } catch (e) {
          console.error('[CORS] Error al procesar errores previos:', e);
        }
      }
    }
  } catch (e) {
    // Ignorar errores en caso de que localStorage no esté disponible
  }
}

/**
 * Versión simplificada para integrar el sistema sin opciones
 * @returns {Object} Sistema CORS configurado
 */
integrateCorsSystem.simple = () => integrateCorsSystem({
  enableTelemetry: true,
  withReactProvider: false,
  autoDetectProblems: true
});

/**
 * Versión sin telemetría para entornos con restricciones de privacidad
 * @returns {Object} Sistema CORS configurado
 */
integrateCorsSystem.private = () => integrateCorsSystem({
  enableTelemetry: false,
  withReactProvider: true,
  autoDetectProblems: true
});

/**
 * Función para usarse en el punto de entrada de la aplicación
 * que configura automáticamente todo el sistema CORS
 */
export function configureCorsSystem() {
  return integrateCorsSystem.simple();
}

// Exportar función directamente y como default
export { integrateCorsSystem };
export default integrateCorsSystem;
